import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, AlertTriangle, TrendingUp, TrendingDown, ChevronRight, Loader2 } from "lucide-react";

const SEV = {
  critical: { color: "#D90429", label: "Critical" },
  warning: { color: "#F5B700", label: "Warning" },
  info: { color: "#0033A0", label: "Info" },
};

export default function MorningBrief({ onAskAgent }) {
  const [brief, setBrief] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/copilot/brief").then(({ data }) => setBrief(data)).catch((e) => setErr(e?.response?.data?.detail || "Failed")); }, []);

  if (err) return null;
  if (!brief) return (
    <div className="mt-8 border border-[#DEE2E6] bg-[#F8F9FA] p-6 text-sm text-[#495057] inline-flex items-center gap-2" data-testid="brief-loading">
      <Loader2 size={14} className="animate-spin" /> Gr8Agentic AI is preparing your morning brief…
    </div>
  );

  const delta = brief.stats.revenue_last7 - brief.stats.revenue_prior7;
  const TrendIcon = delta >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="mt-8 border border-[#0A0A0A] bg-white" data-testid="morning-brief">
      <div className="px-6 py-3 bg-[#0A0A0A] text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#FF4F00]" />
          <div className="font-heading font-semibold tracking-tight">Gr8Agentic Morning Brief</div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
          {new Date(brief.as_of).toLocaleString()}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-0">
        {/* Highlights */}
        <div className="p-6 border-r border-[#DEE2E6]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4F00] font-mono mb-3">Highlights</div>
          {brief.highlights.length === 0 ? (
            <div className="text-sm text-[#495057]">All quiet today. ☕</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {brief.highlights.map((h, i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-2" data-testid={`brief-highlight-${i}`}>
                  <TrendIcon size={14} className={delta >= 0 ? "text-[#0B7A75] mt-0.5" : "text-[#D90429] mt-0.5"} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Alerts */}
        <div className="p-6 border-r border-[#DEE2E6]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4F00] font-mono mb-3">Alerts</div>
          {brief.alerts.length === 0 ? (
            <div className="text-sm text-[#0B7A75]">No alerts — system healthy.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {brief.alerts.map((a, i) => (
                <li key={i} data-testid={`brief-alert-${i}`} className="grid grid-cols-[auto_1fr] gap-2">
                  <AlertTriangle size={14} style={{ color: SEV[a.severity]?.color }} className="mt-0.5" />
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.15em] mr-1.5" style={{ color: SEV[a.severity]?.color }}>{SEV[a.severity]?.label}</span>
                    <span>{a.text}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Suggestions */}
        <div className="p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4F00] font-mono mb-3">Suggested for you</div>
          <ul className="space-y-2 text-sm">
            {brief.suggestions.map((s, i) => (
              <li key={i}>
                <button onClick={() => onAskAgent?.(s.prompt)} data-testid={`brief-suggestion-${i}`}
                  className="w-full text-left flex items-start gap-2 px-3 py-2 border border-[#DEE2E6] hover:border-[#FF4F00] hover:bg-[#FF4F00]/5 transition-colors">
                  <ChevronRight size={14} className="text-[#FF4F00] mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{s.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
